/**
 * A4 — Gruppen-Dashboard (v1.8 WS-Plattform, D-111/D-132). Zeigt den Sub-Scope
 * einer Gruppe: die Gruppen-Dateien (Ordner `Teams/<Gruppe>`), die Mitglieder
 * (Rechte-Union, D-111), die departmentalen Systemlisten (auf die Gruppe
 * gefiltert, provisionierter Team-View) und die Prozess-Vorlagen der Gruppe.
 *
 * Redesign Welle R — die zwei früheren „noch nicht verfügbar"-Panels sind an den
 * Team-Contract verdrahtet (`GET /workspaces/:ws/groups/:id/team-lists`,
 * `ryfeApi.groupTeamLists`, D-146): `lists` = departmentale Systemlisten mit
 * provisioniertem Team-View, `processes` = die on-demand-Materialisierungs-
 * Vorlagen, `forms` = die gruppen-gebundenen Team-Formulare. „＋ Aufgabe" öffnet
 * bei konfigurierten `required_inputs` (D-147) die Instanziierungs-Form und
 * materialisiert (L1 → Serie, L2-standalone → Einzel-Task); der Server erzwingt
 * die Pflicht-Inputs und stempelt `group_id` (Team) über den Prozess.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import { ProcessInstantiateDialog } from "./ProcessInstantiateDialog";

import "./ryfe.scss";

import type {
  GroupMember,
  ProcessRequiredInput,
  RyfeGroup as Group,
  TeamLists,
} from "../api";

type TeamProcess = TeamLists["processes"][number];

const initials = (email: string) =>
  email.replace(/@.*/, "").slice(0, 2).toUpperCase();

export const RyfeGroup = ({ groupId }: { groupId: string }) => {
  const { workspace, groups, group, setGroup } = useRyfe();
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [teamLists, setTeamLists] = useState<TeamLists | null>(null);
  const [notFound, setNotFound] = useState(false);
  // D-147 — Instanziierungs-Form: der Prozess, dessen Pflicht-Inputs abgefragt
  // werden, die Eingaben, Busy/Fehler.
  const [matProc, setMatProc] = useState<TeamProcess | null>(null);
  const [matInputs, setMatInputs] = useState<ProcessRequiredInput[]>([]);
  const [matValues, setMatValues] = useState<Record<string, string>>({});
  const [matBusy, setMatBusy] = useState(false);
  const [matError, setMatError] = useState<string | null>(null);
  // „Aufgabe erstellen"-Quicklink: öffnet das vorhandene Aufgabenformular
  // (Dropdown ALLER Team-Prozesse → materialisieren), statt es zu duplizieren.
  const [taskFormOpen, setTaskFormOpen] = useState(false);

  // Resolve the group from context (loaded per active workspace) and make it the
  // active sub-scope so the switcher reflects the deep-linked group.
  const current: Group | null =
    group?.id === groupId
      ? group
      : groups.find((g) => g.id === groupId) || null;

  useEffect(() => {
    if (current && group?.id !== current.id) {
      setGroup(current);
    }
    // groups may still be loading right after a deep link; only flag notFound
    // once the workspace's groups are present and this id is absent.
    if (
      !current &&
      groups.length > 0 &&
      !groups.some((g) => g.id === groupId)
    ) {
      setNotFound(true);
    } else if (current) {
      setNotFound(false);
    }
  }, [current, group, groups, groupId, setGroup]);

  useEffect(() => {
    if (!workspace || !current) {
      return;
    }
    let cancelled = false;
    ryfeApi
      .listGroupMembers(workspace.id, current.id)
      .then((r) => {
        if (!cancelled) {
          setMembers(r.members);
        }
      })
      .catch(() => undefined);
    if (current.team_folder_id) {
      ryfeApi
        .listFolder(workspace.id, current.team_folder_id)
        .then((r) => {
          if (!cancelled) {
            // Exclude the group's own system folders (KI / Papierkorb / Archiv)
            // from the "files" count — only real content in Teams/<Gruppe>.
            const files = r.files.length;
            const contentFolders = r.folders.filter(
              (f) => !f.role && f.name !== "KI",
            ).length;
            setFileCount(files + contentFolders);
          }
        })
        .catch(() => undefined);
    }
    // D-146 — die departmentalen Systemlisten (Team-View) + Prozess-Vorlagen.
    ryfeApi
      .groupTeamLists(workspace.id, current.id)
      .then((r) => {
        if (!cancelled) {
          setTeamLists(r);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [workspace, current]);

  // D-147 — eine Aufgabe aus einem Prozess instanziieren. Sind Pflicht-Inputs
  // konfiguriert, wird zuerst die kleine Form geöffnet; sonst direkt materialisiert.
  const runMaterialize = async (
    p: TeamProcess,
    body: Record<string, string>,
  ) => {
    if (!workspace) {
      return;
    }
    setMatBusy(true);
    setMatError(null);
    try {
      if (p.level === 1) {
        await ryfeApi.materializeProcessSeries(workspace.id, p.id, body);
      } else {
        await ryfeApi.materializeProcess(workspace.id, p.id, body);
      }
      setMatProc(null);
      setMatInputs([]);
      setMatValues({});
    } catch (e) {
      setMatError((e as Error).message);
    } finally {
      setMatBusy(false);
    }
  };

  const openMaterialize = async (p: TeamProcess) => {
    if (!workspace) {
      return;
    }
    setMatError(null);
    try {
      const detail = await ryfeApi.getProcess(workspace.id, p.id);
      const inputs = detail.required_inputs || [];
      if (inputs.length === 0) {
        void runMaterialize(p, {});
        return;
      }
      setMatProc(p);
      setMatInputs(inputs);
      setMatValues({});
    } catch (e) {
      setMatError((e as Error).message);
    }
  };

  if (notFound) {
    return (
      <div className="ryfe-page">
        <h1 className="ryfe-page__title">Gruppe nicht gefunden</h1>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Diese Gruppe existiert nicht oder gehört zu einem anderen Workspace.
        </p>
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={() => navigate("/")}
        >
          Zur Startseite
        </button>
      </div>
    );
  }

  if (!current) {
    return <div className="ryfe-muted">Lädt…</div>;
  }

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">{current.name}</h1>
      <div className="ryfe-sub">Gruppen-Dashboard · {workspace?.name}</div>

      <div className="ryfe-dash">
        <div className="ryfe-panel">
          <div className="ryfe-panel__title">Gruppen-Dateien</div>
          <div className="ryfe-muted" style={{ padding: "0.4rem 0" }}>
            Ordner <code>Teams/{current.name}</code>
            {fileCount !== null ? ` — ${fileCount} Einträge` : ""}
          </div>
          <button
            className="ryfe-btn--link"
            disabled={!current.team_folder_id}
            onClick={() =>
              current.team_folder_id &&
              navigate(`/files/${current.team_folder_id}`)
            }
          >
            Öffnen →
          </button>
        </div>

        <div className="ryfe-panel">
          <div
            className="ryfe-panel__title"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.5rem",
            }}
          >
            <span>Prozesse (Gruppe)</span>
            <span style={{ display: "flex", gap: "0.4rem" }}>
              {/* Quicklink „Prozess erstellen" — öffnet die Prozesse-Systemliste
                  direkt im Anlage-Modus (?create). */}
              {(() => {
                const pl = (teamLists?.lists || []).find(
                  (l) => l.list_type === "aufgaben.prozesse",
                );
                return pl ? (
                  <button
                    className="ryfe-btn"
                    onClick={() => navigate(`/list/${pl.file_id}?create=1`)}
                  >
                    ＋ Prozess erstellen
                  </button>
                ) : null;
              })()}
              {/* Aufgabenformular: der eine Quicklink, der das vorhandene Formular
                  (Dropdown ALLER Team-Prozesse → materialisieren) öffnet. */}
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={() => setTaskFormOpen(true)}
              >
                ＋ Aufgabe erstellen
              </button>
            </span>
          </div>
          <div className="ryfe-muted" style={{ padding: "0.4rem 0" }}>
            „Aufgabe erstellen" öffnet das Aufgabenformular (Prozess wählen →
            materialisieren). Die Liste unten sind die Vorlagen dieses Teams.
          </div>
          {(teamLists?.processes || []).map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
                padding: "0.15rem 0",
              }}
            >
              <span>
                {p.name}
                {p.level != null ? ` · L${p.level}` : ""}
              </span>
              <button
                className="ryfe-btn--link"
                onClick={() => void openMaterialize(p)}
              >
                ＋ Aufgabe
              </button>
            </div>
          ))}
          {teamLists && teamLists.processes.length === 0 && (
            <div className="ryfe-muted" style={{ padding: 0 }}>
              Keine Prozesse.
            </div>
          )}
        </div>

        <div className="ryfe-panel">
          <div className="ryfe-panel__title">Systemlisten (Gruppe)</div>
          <div className="ryfe-muted" style={{ padding: "0.4rem 0" }}>
            Departmentale Systemlisten, auf diese Gruppe gefiltert.
          </div>
          {(teamLists?.lists || []).map((l) => (
            <div
              key={l.file_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
                padding: "0.15rem 0",
              }}
            >
              <span>{l.name}</span>
              <button
                className="ryfe-btn--link"
                onClick={() => navigate(`/list/${l.file_id}`)}
              >
                Öffnen →
              </button>
            </div>
          ))}
          {teamLists && teamLists.lists.length === 0 && (
            <div className="ryfe-muted" style={{ padding: 0 }}>
              Keine departmentalen Systemlisten.
            </div>
          )}
        </div>

        <div className="ryfe-panel">
          <div className="ryfe-panel__title">Formulare (Gruppe)</div>
          <div className="ryfe-muted" style={{ padding: "0.4rem 0" }}>
            Team-Formulare der departmentalen Listen (gruppen-gestempelt).
          </div>
          {(teamLists?.forms || []).map((f) => (
            <div
              key={f.form_id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "0.5rem",
                padding: "0.15rem 0",
              }}
            >
              <span>{f.name}</span>
              <button
                className="ryfe-btn--link"
                onClick={() => navigate(`/f/${f.form_id}`)}
              >
                Öffnen →
              </button>
            </div>
          ))}
          {teamLists && teamLists.forms.length === 0 && (
            <div className="ryfe-muted" style={{ padding: 0 }}>
              Keine Team-Formulare.
            </div>
          )}
        </div>

        <div className="ryfe-panel">
          <div className="ryfe-panel__title">Mitglieder</div>
          <div className="ryfe-avatars">
            {members.length === 0 && (
              <span className="ryfe-muted" style={{ padding: 0 }}>
                Noch keine Mitglieder.
              </span>
            )}
            {members.map((m) => (
              <span
                key={m.user_id}
                className="ryfe-av"
                title={`${m.email}${m.role === "admin" ? " (Admin)" : ""}`}
              >
                {initials(m.email)}
              </span>
            ))}
          </div>
          <button
            className="ryfe-btn--link"
            style={{ marginTop: "0.5rem" }}
            onClick={() => navigate(`/groups/${current.id}/settings`)}
          >
            Verwalten →
          </button>
        </div>
      </div>

      {/* Aufgabenformular (Quicklink) — das vorhandene Formular: Dropdown ALLER
          Team-Prozesse → Pflichtangaben → materialisieren. */}
      {taskFormOpen && workspace && current && (
        <ProcessInstantiateDialog
          wsId={workspace.id}
          groupId={current.id}
          onClose={() => setTaskFormOpen(false)}
          onDone={() => undefined}
        />
      )}

      {/* D-147 — Instanziierungs-Form: Pflicht-Inputs für „Aufgabe erstellen". */}
      {matProc && (
        <div
          className="ryfe-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Aufgabe aus Vorlage"
          onClick={() => setMatProc(null)}
        >
          <form
            className="ryfe-modal__card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              void runMaterialize(matProc, matValues);
            }}
          >
            <div className="ryfe-panel__title" style={{ margin: 0 }}>
              {matProc.name}
            </div>
            <div className="ryfe-muted" style={{ padding: 0 }}>
              Pflichtangaben für die Instanziierung.
            </div>

            {matInputs.map((ri) => (
              <div key={ri.field}>
                <div className="ryfe-field-label">{ri.label || ri.field}</div>
                {ri.field === "description" ? (
                  <textarea
                    className="ryfe-field-input"
                    value={matValues[ri.field] || ""}
                    onChange={(e) =>
                      setMatValues((v) => ({
                        ...v,
                        [ri.field]: e.target.value,
                      }))
                    }
                  />
                ) : (
                  <input
                    className="ryfe-field-input"
                    type={ri.field === "due_date" ? "date" : "text"}
                    placeholder={ri.contact_type || undefined}
                    value={matValues[ri.field] || ""}
                    onChange={(e) =>
                      setMatValues((v) => ({
                        ...v,
                        [ri.field]: e.target.value,
                      }))
                    }
                  />
                )}
              </div>
            ))}

            {matError && (
              <div className="ryfe-notice ryfe-notice--warn">{matError}</div>
            )}

            <div className="ryfe-modal__actions">
              <button
                type="button"
                className="ryfe-btn ryfe-btn--ghost"
                onClick={() => setMatProc(null)}
              >
                Abbrechen
              </button>
              <span style={{ flex: 1 }} />
              <button
                type="submit"
                className="ryfe-btn ryfe-btn--primary"
                disabled={
                  matBusy ||
                  matInputs.some((ri) => !(matValues[ri.field] || "").trim())
                }
              >
                {matBusy ? "…" : "Aufgabe erstellen"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
